import { useState, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { fetchProducts, fetchGroups, needsRestocking, getProducts } from '@/lib/storage';
import { Product } from '@/lib/types';
import { getAlertSettings } from '@/lib/storage/alertStorage';

export const useDataRefresh = (dismissedAlerts: string[]) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [lowStockProducts, setLowStockProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dataFetchAttempt, setDataFetchAttempt] = useState(0);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const { toast } = useToast();

  const refreshData = useCallback(async () => {
    try {
      setFetchError(null);
      console.log('Refreshing data from remote sources...');
      
      // Add a timeout mechanism
      const timeout = new Promise<null>((_, reject) => {
        setTimeout(() => reject(new Error('Data fetch timeout')), 10000);
      });
      
      // Try to fetch products with timeout
      const productsPromise = fetchProducts(true);
      let remoteProducts;
      
      try {
        remoteProducts = await Promise.race([productsPromise, timeout]) as Product[];
      } catch (error) {
        console.error("Timeout or error fetching products:", error);
        throw new Error('Failed to fetch products: Timeout');
      }
      
      if (!remoteProducts || !Array.isArray(remoteProducts)) {
        throw new Error('Failed to fetch products: Invalid response');
      }
      
      // Set products
      setProducts(remoteProducts);
      console.log(`Loaded ${remoteProducts.length} total products after merging`);
      
      // Check for low stock products
      const settings = getAlertSettings();
      const lowStock = remoteProducts.filter(product => 
        needsRestocking(product) && !dismissedAlerts.includes(product.id)
      );
      setLowStockProducts(lowStock);
      
      // If notifications are enabled and we have low stock products, show a toast
      if (settings.enabled && lowStock.length > 0 && (settings.emailNotifications || settings.phoneNotifications)) {
        toast({
          title: `${lowStock.length} ${lowStock.length === 1 ? 'product' : 'products'} running low`,
          description: "Check your low stock alerts for details",
          duration: 5000,
        });
      }
      
      // Also fetch groups to keep data in sync
      try {
        const groupsPromise = fetchGroups(true);
        await Promise.race([groupsPromise, timeout]);
      } catch (error) {
        console.error("Error or timeout fetching groups:", error);
        // Continue even if groups fetch fails - don't throw
      }
      
      return true;
    } catch (error) {
      console.error("Error refreshing data:", error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setFetchError(errorMessage);
      
      // Fall back to local storage if remote fetch fails
      try {
        const localProducts = getProducts();
        if (localProducts.length > 0) {
          setProducts(localProducts);
          
          // Check for low stock products in local data
          const settings = getAlertSettings();
          const lowStock = localProducts.filter(product => 
            needsRestocking(product) && !dismissedAlerts.includes(product.id)
          );
          setLowStockProducts(lowStock);
          
          toast({
            variant: "default",
            title: "Using local data",
            description: "Could not connect to cloud storage. Using local data instead.",
          });
          
          return true;
        }
      } catch (localError) {
        console.error("Error loading local data:", localError);
      }
      
      toast({
        variant: "destructive",
        title: "Error loading data",
        description: "There was an error loading your data. Please try again.",
      });
      return false;
    } finally {
      // Ensure loading state is set to false when done, regardless of success or failure
      setIsLoading(false);
    }
  }, [dismissedAlerts, toast]);

  const incrementFetchAttempt = useCallback(() => {
    setDataFetchAttempt(prev => prev + 1);
  }, []);

  return {
    products,
    lowStockProducts,
    isLoading,
    setIsLoading,
    fetchError,
    dataFetchAttempt,
    incrementFetchAttempt,
    refreshData
  };
};
